import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '@hum/icm-app/src/state';
import { CreditBoxSummary } from '@hum/icm-app/src/components/CreditBoxSummary';
import { formatDate } from '@hum/icm-app/src/utils/format';
import { InfoSection, Text, TextType } from '@hum/ui-library';
import { useCurrentCompany } from '@hum/icm-app/src/hooks/useCurrentCompany';
import {
  useApiMutation,
  createCreditBoxReports,
  useApiQuery,
  getCreditBoxReports,
} from '@hum/api';

export const useCreditBoxReport = () => {
  const company = useCurrentCompany();
  const companyId = company.data!.uuid;

  const isDownloadingCreditBoxReport = useSelector(
    (state: AppState) => state.isDownloadingCreditBoxReport
  );

  const {
    data: creditBoxReports,
    isLoading: isLoadingCreditBoxReports,
  } = useApiQuery(getCreditBoxReports, {
    params: { companyId },
  });

  const creditBoxReport = {
    loaded: !isLoadingCreditBoxReports,
    data: creditBoxReports,
  };

  return {
    creditBoxReport,
    isDownloadingCreditBoxReport,
    companyId,
  };
};

export const CreditBoxInvestors = ({ companyId }: { companyId: string }) => {
  const { mutate: pullCreditBoxReports } = useApiMutation(
    createCreditBoxReports
  );

  const {
    data: creditBoxReports,
    isLoading: isLoadingCreditBoxReports,
  } = useApiQuery(getCreditBoxReports, {
    params: { companyId },
  });

  const creditBoxReport = {
    loaded: !isLoadingCreditBoxReports,
    data: creditBoxReports,
  };

  const handleClick = useCallback(() => {
    pullCreditBoxReports({ companyId });
  }, [companyId]);

  return (
    <InfoSection
      testId="admin::credit-box-investors"
      title="SmartRaise credit boxes"
      primaryAction={{
        text: 'Recalculate all',
        onClick: handleClick,
      }}
    >
      {creditBoxReport.loaded && creditBoxReport.data ? (
        creditBoxReport.data.map((report) => (
          <div
            className="p-5 mt-5 text-center bg-surface-subdued rounded-default"
            key={report.id}
          >
            <CreditBoxSummary
              reportId={report.id}
              reportType={report.type}
              companyId={companyId}
              reportDate={formatDate(report.createdAt)}
              reportStatus={report.status}
              reportRating={report.rating}
            />
          </div>
        ))
      ) : (
        <div className="p-5 mt-5 text-center bg-surface-subdued rounded-default">
          <Text type={TextType.BODY_M}>No credit box matches, yet.</Text>
        </div>
      )}
    </InfoSection>
  );
};
